<template>
  <v-dialog v-model="dialog" max-width="500" :persistent="loading" scrollable>
    <v-card>
      <v-card-title>Novo Lançamento</v-card-title>
      <v-card-text>
        <v-form v-model="valid" @submit.prevent>
          <div class="text-caption text--secondary mb-1">Tipo</div>
          <v-btn-toggle
            v-model="form.type"
            mandatory
            dense
            class="d-flex mb-4"
            :disabled="loading"
          >
            <v-btn
              value="INCOME"
              class="flex-1"
              :color="form.type === 'INCOME' ? 'success' : undefined"
            >
              <v-icon
                left
                small
                :color="form.type === 'INCOME' ? 'white' : 'success'"
              >
                mdi-arrow-down-bold
              </v-icon>
              <span :class="form.type === 'INCOME' ? 'white--text' : undefined">
                Entrada
              </span>
            </v-btn>
            <v-btn
              value="EXPENSE"
              class="flex-1"
              :color="form.type === 'EXPENSE' ? 'error' : undefined"
            >
              <v-icon
                left
                small
                :color="form.type === 'EXPENSE' ? 'white' : 'error'"
              >
                mdi-arrow-up-bold
              </v-icon>
              <span
                :class="form.type === 'EXPENSE' ? 'white--text' : undefined"
              >
                Saída
              </span>
            </v-btn>
          </v-btn-toggle>

          <v-combobox
            v-model="form.description"
            :items="descriptionSuggestions"
            item-text="description"
            item-value="description"
            :return-object="false"
            :loading="loadingDescriptions"
            label="Descrição"
            outlined
            dense
            hide-selected
            :rules="[(v) => !!descriptionText(v) || 'Descrição é obrigatória']"
            :disabled="loading"
            @change="onDescriptionChange"
          />
          <CashCategoryField
            v-if="dialog"
            ref="categoryField"
            v-model="form.categoryId"
            :type="form.type"
            :disabled="loading"
          />
          <v-text-field
            v-model="form.expectedAmount"
            label="Valor"
            type="number"
            step="0.01"
            min="0"
            prefix="R$"
            outlined
            dense
            :rules="amountRules"
            :disabled="loading"
            @paste="onAmountPaste($event, 'expectedAmount')"
            @blur="onAmountBlur('expectedAmount')"
            @input="syncSettledFields"
          />
          <v-text-field
            v-model="form.dueDate"
            label="Data de vencimento"
            type="date"
            outlined
            dense
            :disabled="loading"
            @input="onDueDateChange"
          />

          <v-switch
            v-model="form.markAsSettled"
            :label="settledSwitchLabel"
            dense
            hide-details
            class="mt-2"
            :disabled="loading"
            @change="onMarkAsSettledChange"
          />

          <template v-if="form.markAsSettled">
            <v-text-field
              v-model="form.settledAmount"
              :label="form.type === 'EXPENSE' ? 'Valor pago' : 'Valor recebido'"
              type="number"
              step="0.01"
              min="0"
              prefix="R$"
              outlined
              dense
              class="mt-2"
              :rules="amountRules"
              :disabled="loading"
              @paste="onAmountPaste($event, 'settledAmount')"
              @blur="onAmountBlur('settledAmount')"
            />
            <v-text-field
              v-model="form.settledAt"
              :label="
                form.type === 'EXPENSE'
                  ? 'Data do pagamento'
                  : 'Data do recebimento'
              "
              type="date"
              outlined
              dense
              hide-details
              :rules="[(v) => !!v || 'Data é obrigatória']"
              :disabled="loading"
            />
            <SettledDateChips
              v-model="form.settledAt"
              :due-date="form.dueDate"
              :disabled="loading"
            />
            <v-textarea
              v-model="form.notes"
              label="Observações"
              outlined
              dense
              rows="2"
              :disabled="loading"
            />
          </template>
        </v-form>
        <v-alert v-if="error" type="error" dense text class="mt-2">
          {{ error }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text small @click="close" :disabled="loading">Cancelar</v-btn>
        <v-btn
          color="primary"
          depressed
          small
          :loading="loading"
          :disabled="!valid"
          @click="submit"
        >
          Salvar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import CASH from "@/services/ultimate/cash.js";
import { parseBrazilianCurrency } from "@/utils/parseCurrency";
import CashCategoryField from "@/components/ultimate/cash/CashCategoryField.vue";
import { withLocalDateISO } from "@/utils/dateOnlyToLocalISO";
import SettledDateChips from "@/components/ultimate/cash/SettledDateChips.vue";

export default {
  name: "CashEntryForm",
  components: {
    SettledDateChips,
    CashCategoryField,
  },
  data() {
    return {
      dialog: false,
      loading: false,
      loadingDescriptions: false,
      valid: false,
      error: null,
      descriptionSuggestions: [],
      form: this.getDefaultForm(),
      amountRules: [
        (v) => (v !== "" && v != null) || "Valor é obrigatório",
        (v) => parseFloat(v) > 0 || "Valor deve ser maior que zero",
      ],
    };
  },
  computed: {
    settledSwitchLabel() {
      return this.form.type === "EXPENSE"
        ? "Marcar como pago"
        : "Marcar como recebido";
    },
    descriptionReference() {
      if (!this.form.dueDate) return moment().format("MM/YYYY");
      return moment(this.form.dueDate).format("MM/YYYY");
    },
  },
  methods: {
    getDefaultForm() {
      const today = moment().format("YYYY-MM-DD");
      return {
        type: "INCOME",
        description: "",
        categoryId: null,
        expectedAmount: null,
        dueDate: today,
        markAsSettled: false,
        settledAmount: null,
        settledAt: today,
        notes: "",
      };
    },
    applyParsedAmount(field, rawValue) {
      const { amount, isNegative } = parseBrazilianCurrency(rawValue);
      if (amount == null) return false;

      this.form[field] = amount;
      if (isNegative) this.form.type = "EXPENSE";
      this.syncSettledFields();
      return true;
    },
    onAmountPaste(event, field) {
      const text = event.clipboardData.getData("text");
      if (this.applyParsedAmount(field, text)) {
        event.preventDefault();
      }
    },
    onAmountBlur(field) {
      this.applyParsedAmount(field, this.form[field]);
    },
    syncSettledFields() {
      if (!this.form.markAsSettled) return;
      this.form.settledAmount = this.form.expectedAmount;
      this.form.settledAt = this.form.dueDate || moment().format("YYYY-MM-DD");
    },
    onMarkAsSettledChange(value) {
      if (value) this.syncSettledFields();
    },
    onDueDateChange() {
      this.syncSettledFields();
      this.loadDescriptions();
    },
    descriptionText(value) {
      if (typeof value === "string") return value.trim();
      if (value && typeof value.description === "string") {
        return value.description.trim();
      }
      return "";
    },
    onDescriptionChange(value) {
      const description = this.descriptionText(value);
      if (this.form.description !== description) {
        this.form.description = description;
      }
      if (!description) return;

      const match = this.descriptionSuggestions.find(
        (item) => item.description === description
      );
      if (!match) return;

      if (match.type) this.form.type = match.type;
      if (match.categoryId && this.$refs.categoryField) {
        this.$refs.categoryField.apply(match.categoryId);
      }
    },
    async loadDescriptions() {
      try {
        this.loadingDescriptions = true;
        const response = await CASH.getDescriptions({
          reference: this.descriptionReference,
        });
        this.descriptionSuggestions = response.descriptions || [];
      } catch (error) {
        console.error(error);
        this.descriptionSuggestions = [];
      } finally {
        this.loadingDescriptions = false;
      }
    },
    open() {
      this.form = this.getDefaultForm();
      this.error = null;
      this.dialog = true;
      this.loadDescriptions();
    },
    close() {
      this.dialog = false;
    },
    async submit() {
      try {
        this.loading = true;
        this.error = null;
        this.applyParsedAmount("expectedAmount", this.form.expectedAmount);
        if (this.form.markAsSettled) {
          this.applyParsedAmount("settledAmount", this.form.settledAmount);
        }
        await CASH.create(
          withLocalDateISO({
            ...this.form,
            description: this.descriptionText(this.form.description),
          })
        );
        this.$emit("created");
        this.close();
      } catch (error) {
        this.error = error.message || "Erro ao criar lançamento";
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.$parent.$on("cash-entry-create", this.open);
  },
  beforeDestroy() {
    this.$parent.$off("cash-entry-create", this.open);
  },
};
</script>
